import {Criteria} from '../types/criteria.model';
import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {LoadComponent, PaginationComponent} from '@brunata-angular/ui';
import {Sorting} from '@brunata-angular/ui';
import {
    BehaviorSubject,
    combineLatest,
    map,
    Observable,
    of,
    startWith,
    Subject,
    switchMap,
    tap,
} from 'rxjs';
import {ConsumptionReportService} from '../consumption-report.service';
import {ConsumptionReportDetails} from '../types/consumption-report.model';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {InputSearchComponent} from '@shared/ui/input-search/input-search.component';
import {BuildingTableComponent} from './building-table/building-table.component';
import {LocationTableComponent} from './location-table/location-table.component';
import {MeterTableComponent} from './meter-table/meter-table.component';
import {
    comparator,
    composeComparators,
    includesIgnoreCase,
    keyComparator,
    TranslatePipe,
} from '@brunata-angular/util';

export type ConsumptionReportsView = {
    consumptionReports: ConsumptionReportDetails[];
    criteria: Criteria;
    loading: boolean;
};

@Component({
    selector: 'app-consumption-result-table',
    standalone: true,
    templateUrl: './consumption-result-table.component.html',
    styleUrl: './consumption-result-table.component.scss',
    imports: [
        CommonModule,
        TranslatePipe,
        InputSearchComponent,
        ReactiveFormsModule,
        LoadComponent,
        BuildingTableComponent,
        LocationTableComponent,
        MeterTableComponent,
        PaginationComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsumptionResultTableComponent {
    @Input() set criteria(criteria: Criteria | null) {
        if (criteria) {
            this.criteria$.next(criteria);
        }
    }
    @Output() backToCriteria: EventEmitter<void> = new EventEmitter<void>();
    private criteria$ = new Subject<Criteria>();
    searchControl = new FormControl<string>('');
    sorting: Subject<Sorting> = new Subject<Sorting>();
    page: BehaviorSubject<number> = new BehaviorSubject<number>(1);
    pageSize = 10;

    readonly view$: Observable<ConsumptionReportsView> = this.criteria$.pipe(
        tap(() => 'criteria'),
        switchMap(criteria =>
            combineLatest([
                this.getConsumptionReports(criteria),
                this.searchControl.valueChanges.pipe(startWith('')),
                this.sorting.pipe(startWith({column: '', direction: 'asc'} as Sorting)),
            ]).pipe(
                map(([reports, filter, sorting]) => ({
                    consumptionReports: this.filterValues(reports.consumptionReports, filter).sort(
                        sorting.column === ''
                            ? // Standard sorting
                              composeComparators([
                                  comparator({
                                      selector: document => document.proBraLoc,
                                      order: 'asc',
                                  }),
                                  comparator({
                                      selector: document => document.alloUnitType,
                                      order: 'asc',
                                  }),
                                  comparator({selector: document => document.unit, order: 'asc'}),
                                  comparator({
                                      selector: document => document.meterSequenceNo,
                                      order: 'asc',
                                  }),
                              ])
                            : // Column sorting
                              keyComparator(
                                  consumptionReports =>
                                      consumptionReports[
                                          sorting.column as keyof ConsumptionReportDetails
                                      ],
                                  sorting.direction ?? 'asc',
                              ),
                    ),
                    criteria: criteria,
                    loading: reports.loading,
                })),
            ),
        ),
    );

    constructor(private consumptionReportService: ConsumptionReportService) {}

    private filterValues(
        consumptionReports: ConsumptionReportDetails[],
        filter: string | null,
    ): ConsumptionReportDetails[] {
        return consumptionReports.filter(meterDetails =>
            Object.values(meterDetails).some(
                data => typeof data === 'string' && includesIgnoreCase(data, filter),
            ),
        );
    }

    private getConsumptionReports(criteria: Criteria): Observable<ConsumptionReportsView> {
        if (
            criteria.buildingNo != null &&
            criteria.startDate != null &&
            criteria.endDate != null &&
            criteria.deviceTypes != null &&
            criteria.deviceTypes.length > 0 &&
            criteria.groupLevel != null &&
            criteria.periodInterval != null
        ) {
            return this.consumptionReportService
                .getConsumptionReports(
                    criteria.buildingNo,
                    criteria.startDate,
                    criteria.endDate,
                    criteria.deviceTypes.map(device => ({
                        allocationUnit: device.allocationUnit,
                        meterType: device.meterType ?? null,
                        unit: device.unit ?? null,
                    })),
                    criteria.groupLevel,
                    criteria.periodInterval,
                )
                .pipe(
                    map(it => ({
                        consumptionReports: it,
                        criteria: criteria,
                        loading: false,
                    })),
                    startWith({
                        consumptionReports: [],
                        criteria: criteria,
                        loading: true,
                    }),
                );
        }
        return of({
            consumptionReports: [],
            criteria: criteria,
            loading: false,
        });
    }

    export(criteria: Criteria): void {
        if (
            criteria.buildingNo != null &&
            criteria.startDate != null &&
            criteria.endDate != null &&
            criteria.deviceTypes != null &&
            criteria.deviceTypes.length > 0 &&
            criteria.groupLevel != null &&
            criteria.periodInterval != null
        ) {
            this.consumptionReportService.downloadFile(
                criteria.buildingNo,
                criteria.startDate,
                criteria.endDate,
                criteria.deviceTypes.map(device => ({
                    allocationUnit: device.allocationUnit,
                    meterType: device.meterType ?? null,
                    unit: device.unit ?? null,
                })),
                criteria.groupLevel,
                criteria.periodInterval,
            );
        }
    }

    navBackToCriteria(): void {
        this.searchControl.setValue('');
        this.backToCriteria.emit();
    }

    setSorting(sorting: Sorting): void {
        this.sorting.next(sorting);
    }

    setPage(page: number): void {
        this.page.next(page);
    }
}
